'use client';

import { useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { IFormMetadataProps } from '@/components/Form/types';
import { Modal } from '@/components/Modal/Modal';
import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { useDevice } from '@/hooks/useDevice';
import { parsePhone } from '@/tools/dynamics';
import { getNumberIdsFromString } from '@/tools/forms';
import { DynamicsLeadSource, DynamicsLeadType } from '@/types/dynamicsForms';
import { FormTag } from '@/types/forms';
import { useCustomSearchParams, useFormApi } from '@lamesarv-sdk/hooks';
import { IFormInput } from '@lamesarv-sdk/types';

import { GeneralForm } from './FormTypes/General';
import { InfluencerForm } from './FormTypes/Influencer';
import { generalSchema, influencerSchema } from './schemas';
import { IEventRegistrationFormProps } from './types';

const getHashSanitized = (hash?: string) => {
  if (!hash || hash?.trim() === '#') return undefined;
  return hash.replaceAll('#', '').trim().toLowerCase();
};

const removeHash = () => {
  if (typeof window === 'undefined') return;
  if ('replaceState' in window.history) {
    window.history.replaceState('', window.document.title, window.location.pathname + window.location.search);
  }
};

// --

interface IInventoryForm extends IFormMetadataProps {
  leadType: string;
  leadSource: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  contact: string;
  product: string;
  comments: string;
  terms: boolean;
  subscribe: boolean;
}

// --

const FormReset = ({ wasSuccessful }: { wasSuccessful: boolean }) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!wasSuccessful) return;

    reset();
    reset({
      phone: '',
    });
  }, [wasSuccessful]);

  return null;
};

// --

const mapTypes = {
  general: GeneralForm,
  influencer: InfluencerForm,
};

const mapSchemas = {
  general: generalSchema,
  influencer: influencerSchema,
};

const mapTags = {
  general: 671,
  influencer: 670,
};

const FormBase = (props: IEventRegistrationFormProps) => {
  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();
  const { fetchData: getDevice } = useDevice({ metadata: true, skipFetch: true });
  const { context } = useContext(LayoutContext);

  const { env } = useEnv();

  const _loading = loading || loadingLegacy || false;
  const _error = error || errorLegacy || false;
  const _submitted = submitted || submittedLegacy || false;

  const wasSuccessful = !_loading && !_error && _submitted;
  const wasError = !_loading && _error && _submitted;

  const onSubmit = async (data: IInventoryForm, metadata?: Record<string, unknown>) => {
    const tagId = mapTags[props.form_type] ?? mapTags.general;
    const formMappings = [
      {
        DataverseFieldName: 'lmrv_formtype',
        DataverseFieldValue: props.form_type || 'general',
      },
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: data.leadType || props.custom_form_leadtype || String(DynamicsLeadType.buyer),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource.website),
      },
      {
        DataverseFieldName: 'firstname',
        DataverseFieldValue: data.firstName || '',
      },
      {
        DataverseFieldName: 'lastname',
        DataverseFieldValue: data.lastName || '',
      },
      {
        DataverseFieldName: 'emailaddress1',
        DataverseFieldValue: data.email || '',
      },
      {
        DataverseFieldName: 'mobilephone',
        DataverseFieldValue: data.phone ? parsePhone(data.phone) : '',
      },
      {
        FormFieldName: 'attendance',
        DataverseFieldName: 'lmrv_attendance',
      },
      {
        FormFieldName: 'interests',
        DataverseFieldName: 'lmrv_interests',
      },
      {
        FormFieldName: 'company_name',
        DataverseFieldName: 'companyname',
      },
      {
        FormFieldName: 'website_url',
        DataverseFieldName: 'websiteurl',
      },
      {
        FormFieldName: 'followers_number',
        DataverseFieldName: 'lmrv_followersnumber',
      },
      {
        FormFieldName: 'content_type',
        DataverseFieldName: 'lmrv_contenttype',
      },
      {
        DataverseFieldName: 'lmrv_comments',
        DataverseFieldValue: data.comments || '',
      },
      {
        DataverseFieldName: 'lmrv_accepttermsandconditions',
        DataverseFieldValue: data.terms ? '1' : '0',
      },
      {
        DataverseFieldName: 'lmrv_subscribe',
        DataverseFieldValue: data.subscribe ? '1' : '0',
      },
      {
        DataverseFieldName: 'crbdb_segment',
        DataverseFieldValue: props.dynamics_segment,
      },
    ];

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      const device = await getDevice();
      data.ip = device.ip;
      data.user_agent = device.userAgent;
      data.platform = device.platform;
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!context.geoLocation) {
      data.geo_coordinates = context.geoLocation.join(',');
    }

    if (env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE && env.ENABLE_FORMS_CAPTCHA) {
      data.captcha_score = metadata?.captchaScore ? Number(metadata?.captchaScore) : undefined;
      data.captcha_threshold = metadata?.captchaThreshold ? Number(metadata?.captchaThreshold) : undefined;
    }

    if (env.ENABLE_FORMS_DYNAMICS) {
      await sendForm({
        formId: `#${props.custom_id || 'eventRegistrationForm'}`,
        mappings: formMappings,
        dynamicsFormId: env.DYNAMICS_FORM_ID_EVENT || props.custom_dynamics_id,
      });
    }

    if (env.ENABLE_FORMS_LEGACY) {
      const customTagIds = getNumberIdsFromString(props.custom_active_campaign_tag_ids);
      const customListIds = getNumberIdsFromString(props.custom_active_campaign_list_ids);

      await sendFormLegacy({
        ...data,
        source_url: window.location.href,
        extra_tag_ids: [tagId, ...customTagIds, FormTag.GENERIC_EVENT_REGISTRATION],
        extra_list_ids: customListIds,
      } as unknown as IFormInput);
    }
  };

  useEffect(() => {
    if (wasSuccessful && props.onSuccess) {
      props.onSuccess();
    }
  }, [wasSuccessful]);

  const FormTyped = mapTypes[props.form_type] ?? mapTypes.general;
  const formSchema = mapSchemas[props.form_type] ?? mapSchemas.general;

  return (
    <CommonForm
      id={props.custom_id || 'eventRegistrationForm'}
      schema={formSchema}
      onSubmit={onSubmit}
      isDisabled={_loading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-event-registration` : 'form-event-registration'}
      className="font-helvetica my-8 md:my-16 px-6 md:px-10 lg:container lg:mx-auto"
    >
      <CommonHidden name="formId" value={props.custom_active_campaign_id || '00'} />
      <CommonHidden name="formType" value={props.custom_form_type || 'super_b_rv_show'} />
      <CommonHidden name="leadType" value={props.custom_form_leadtype || 'buyer'} />
      <CommonHidden name="leadSource" value={props.custom_form_leadsource || 'lamesarv.com'} />
      <FormTyped {...props} wasError={wasError} wasSuccessful={wasSuccessful} />
      <FormReset wasSuccessful={wasSuccessful} />
    </CommonForm>
  );
};

export const GenericEventRegistrationForm = (props: IEventRegistrationFormProps) => {
  const { searchParams } = useCustomSearchParams();
  const [modalOpen, setModalOpen] = useState(false);

  const hashParameter = getHashSanitized(props?.modal_hash_parameter);

  const onHashChanged = () => {
    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);

      if (hashParameter) {
        setModalOpen(hashLocation === hashParameter);
      }
    }
  };

  useEffect(() => {
    let modalOpenStatus: boolean = false;

    if (props.modal && props.modal_url_parameter && !!searchParams.get(props.modal_url_parameter)) {
      modalOpenStatus = true;
    }

    if (typeof window !== 'undefined') {
      const hashLocation = getHashSanitized(window?.location?.hash);
      const hashParameter = getHashSanitized(props?.modal_hash_parameter);

      if (!!hashParameter && hashLocation === hashParameter) {
        modalOpenStatus = true;
      }
    }

    setModalOpen(modalOpenStatus);

    // Hash Change Control
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', onHashChanged);
      return () => {
        window.removeEventListener('hashchange', onHashChanged);
      };
    }
  }, []);

  const onClose = () => {
    setModalOpen(false);
    if (hashParameter) {
      removeHash();
    }
  };

  const onSuccess = () => {
    setTimeout(() => {
      setModalOpen(false);
      if (hashParameter) {
        removeHash();
      }
    }, 2000);
  };

  if (props.modal) {
    return (
      <Modal isOpen={modalOpen} onClose={onClose} useMaxWidth={false} classesClose="right-2 top-2 md:right-8 md:top-8">
        <FormBase {...props} onSuccess={onSuccess} classes={twMerge('border-none', props.classes)} />
      </Modal>
    );
  }

  return <FormBase {...props} />;
};
